import { DayAvailability, Interval } from './DayAvailability'
import { Schedule, defaultRules } from './Schedule'

/**
 * Represents a date-specific hour entry for a specific user
 */
export interface UserDateSpecificHour {
  userId: string
  userName: string
  userPhoto?: string
  date: string // format: 'YYYY-MM-DD'
  intervals: Interval[]
}

/**
 * Represents a grouped date entry containing multiple users' hours for the same date
 */
export interface GroupedDateEntry {
  date: string
  users: {
    userId: string
    userName: string
    userPhoto?: string
    intervals: Interval[]
  }[]
}

/**
 * User info for display purposes
 */
export interface BulkEditUser {
  id: string
  name: string
  profilePhoto?: string
}

/**
 * MultiUserSchedule - Used exclusively for bulk edit operations
 * 
 * This class manages date-specific hours for multiple users while preserving
 * individual user data. It provides a combined view for UI while maintaining
 * separate schedules per user internally.
 */
export class MultiUserSchedule {
  // Store individual schedules per user (preserves original data)
  private userSchedules: Map<string, Schedule> = new Map()
  
  // Store user info for display
  private users: Map<string, BulkEditUser> = new Map()
  
  // Common weekly schedule for bulk edit (shared across users)
  weeklySchedule: DayAvailability[] = []
  
  // Timezone for the bulk schedule
  timezone: string = ''
  
  // Calendar ID this bulk edit applies to
  calendarId: string = ''

  constructor() {
    // Initialize with default weekly schedule
    this.weeklySchedule = defaultRules.map(rule => new DayAvailability({
      ...rule,
      intervals: [...rule.intervals]
    }))
  }

  /**
   * Initialize the multi-user schedule from selected users and their existing schedules
   */
  initializeFromUsers(
    users: BulkEditUser[],
    schedules: Schedule[],
    calendarId: string,
    defaultTimezone: string
  ): void {
    this.calendarId = calendarId
    this.timezone = defaultTimezone

    // Store user info
    users.forEach(user => {
      this.users.set(user.id, user)
    })

    // Store individual schedules per user
    schedules.forEach(schedule => {
      if (schedule.userId && this.users.has(schedule.userId)) {
        this.userSchedules.set(schedule.userId, new Schedule({
          ...schedule,
          rules: schedule.rules.map(rule => new DayAvailability({
            ...rule,
            intervals: rule.intervals.map(i => ({ ...i }))
          }))
        }))
        
        // Use the first schedule's timezone as default if not set
        if (!this.timezone && schedule.timezone) {
          this.timezone = schedule.timezone
        }
      }
    })

    // Initialize users without schedules with empty schedules
    users.forEach(user => {
      if (!this.userSchedules.has(user.id)) {
        this.userSchedules.set(user.id, new Schedule({
          userId: user.id,
          timezone: this.timezone,
          calendarIds: calendarId ? [calendarId] : [],
          rules: [],
          isCustom: true,
          isCreated: false,
        }))
      }
    })
  }

  /**
   * Get all users in this bulk edit
   */
  getUsers(): BulkEditUser[] {
    return Array.from(this.users.values())
  }

  /**
   * Get user info by ID
   */
  getUser(userId: string): BulkEditUser | undefined {
    return this.users.get(userId)
  }

  /**
   * Get all date-specific hours grouped by date (for UI display)
   * This provides a combined view showing all users' hours per date
   */
  getGroupedDateSpecificHours(): GroupedDateEntry[] {
    const dateMap = new Map<string, GroupedDateEntry>()

    this.userSchedules.forEach((schedule, userId) => {
      const user = this.users.get(userId)
      if (!user) return

      const dateHours = schedule.getDateSpecificHours()
      dateHours.forEach(rule => {
        if (!rule.date) return

        if (!dateMap.has(rule.date)) {
          dateMap.set(rule.date, {
            date: rule.date,
            users: []
          })
        }

        const entry = dateMap.get(rule.date)!
        entry.users.push({
          userId: user.id,
          userName: user.name,
          userPhoto: user.profilePhoto,
          intervals: rule.intervals.map(i => ({ ...i }))
        })
      })
    })

    // Sort by date
    return Array.from(dateMap.values()).sort((a, b) => 
      a.date.localeCompare(b.date)
    )
  }

  /**
   * Get all date-specific hours as a flat list with user info
   */
  getAllUserDateSpecificHours(): UserDateSpecificHour[] {
    const result: UserDateSpecificHour[] = []

    this.userSchedules.forEach((schedule, userId) => {
      const user = this.users.get(userId)
      if (!user) return

      const dateHours = schedule.getDateSpecificHours()
      dateHours.forEach(rule => {
        if (!rule.date) return

        result.push({
          userId: user.id,
          userName: user.name,
          userPhoto: user.profilePhoto,
          date: rule.date,
          intervals: rule.intervals.map(i => ({ ...i }))
        })
      })
    })

    // Sort by date, then by user name
    return result.sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date)
      if (dateCompare !== 0) return dateCompare
      return a.userName.localeCompare(b.userName)
    })
  }

  /**
   * Add date-specific hours for all selected users
   * Used when adding new date-specific hours in bulk edit mode
   */
  addDateSpecificHoursForAllUsers(date: string, intervals: Interval[]): void {
    this.userSchedules.forEach((schedule, userId) => {
      const newRule = new DayAvailability({
        type: 'date',
        date,
        intervals: intervals.map(i => ({ ...i })),
        isActive: true
      })

      // Check if date already exists for this user
      const existingIndex = schedule.rules.findIndex(
        r => r.type === 'date' && r.date === date
      )

      if (existingIndex !== -1) {
        // Update existing
        schedule.rules[existingIndex] = newRule
      } else {
        // Add new
        schedule.rules.push(newRule)
      }
    })
  }

  /**
   * Add date-specific hours for selected users only
   * Used when adding new date-specific hours with specific user selection
   */
  addDateSpecificHoursForSelectedUsers(date: string, intervals: Interval[], userIds: string[]): void {
    userIds.forEach(userId => {
      const schedule = this.userSchedules.get(userId)
      if (!schedule) return

      const newRule = new DayAvailability({
        type: 'date',
        date,
        intervals: intervals.map(i => ({ ...i })),
        isActive: true
      })

      // Check if date already exists for this user
      const existingIndex = schedule.rules.findIndex(
        r => r.type === 'date' && r.date === date
      )

      if (existingIndex !== -1) {
        // Update existing
        schedule.rules[existingIndex] = newRule
      } else {
        // Add new
        schedule.rules.push(newRule)
      }
    })
  }

  /**
   * Update date-specific hours for a specific user
   * Used when editing individual user's hours
   */
  updateDateSpecificHoursForUser(
    userId: string,
    date: string,
    intervals: Interval[]
  ): void {
    const schedule = this.userSchedules.get(userId)
    if (!schedule) return

    const newRule = new DayAvailability({
      type: 'date',
      date,
      intervals: intervals.map(i => ({ ...i })),
      isActive: true
    })

    const existingIndex = schedule.rules.findIndex(
      r => r.type === 'date' && r.date === date
    )

    if (existingIndex !== -1) {
      schedule.rules[existingIndex] = newRule
    } else {
      schedule.rules.push(newRule)
    }
  }

  /**
   * Delete date-specific hours for a specific user
   */
  deleteDateSpecificHoursForUser(userId: string, date: string): void {
    const schedule = this.userSchedules.get(userId)
    if (!schedule) return

    const index = schedule.rules.findIndex(
      r => r.type === 'date' && r.date === date
    )

    if (index !== -1) {
      schedule.rules.splice(index, 1)
    }
  }

  /**
   * Delete date-specific hours for all users on a specific date
   * Used when deleting a common date entry
   */
  deleteDateSpecificHoursForAllUsers(date: string): void {
    this.userSchedules.forEach(schedule => {
      const index = schedule.rules.findIndex(
        r => r.type === 'date' && r.date === date
      )

      if (index !== -1) {
        schedule.rules.splice(index, 1)
      }
    })
  }

  /**
   * Check if a date-specific hour is common across all users
   */
  isDateCommonAcrossUsers(date: string): boolean {
    let count = 0
    this.userSchedules.forEach(schedule => {
      const hasDate = schedule.rules.some(
        r => r.type === 'date' && r.date === date
      )
      if (hasDate) count++
    })
    return count === this.userSchedules.size
  }

  /**
   * Get users who have date-specific hours on a given date
   */
  getUsersWithDateSpecificHours(date: string): BulkEditUser[] {
    const result: BulkEditUser[] = []
    
    this.userSchedules.forEach((schedule, userId) => {
      const hasDate = schedule.rules.some(
        r => r.type === 'date' && r.date === date
      )
      if (hasDate) {
        const user = this.users.get(userId)
        if (user) result.push(user)
      }
    })

    return result
  }

  /**
   * Set the weekly schedule (applies to all users)
   */
  setWeeklySchedule(rules: DayAvailability[]): void {
    this.weeklySchedule = rules.map(rule => new DayAvailability({
      ...rule,
      intervals: rule.intervals.map(i => ({ ...i }))
    }))
  }

  /**
   * Set timezone (applies to all users)
   */
  setTimezone(timezone: string): void {
    this.timezone = timezone
  }

  /**
   * Convert to individual schedules for saving
   * Each user gets their own custom schedule with:
   * - Common weekly hours from the bulk edit
   * - Their own preserved date-specific hours
   */
  toIndividualSchedules(): Schedule[] {
    const result: Schedule[] = []

    this.userSchedules.forEach((schedule, userId) => {
      // Get weekly rules from the common weekly schedule
      const weeklyRules = this.weeklySchedule
        .filter(r => r.type === 'wday')
        .map(r => new DayAvailability({
          ...r,
          intervals: r.intervals.map(i => ({ ...i }))
        }))

      // Get date-specific rules from user's individual schedule
      const dateRules = schedule.rules
        .filter(r => r.type === 'date')
        .map(r => new DayAvailability({
          ...r,
          intervals: r.intervals.map(i => ({ ...i }))
        }))

      // Create new schedule combining both
      const newSchedule = new Schedule({
        userId,
        timezone: this.timezone,
        calendarIds: this.calendarId ? [this.calendarId] : [],
        rules: [...weeklyRules, ...dateRules],
        isCustom: true,
        isCreated: false,
        name: 'Custom Schedule',
      })

      result.push(newSchedule)
    })

    return result
  }

  /**
   * Convert to individual schedules for selective saving (US5)
   * @param updateWeekly - Whether to include/update weekly hours
   * @param updateDateSpecific - Whether to include/update date-specific hours
   * @param existingSchedules - Map of userId to their existing Schedule (for preserving unmodified sections)
   */
  toIndividualSchedulesSelective(
    updateWeekly: boolean,
    updateDateSpecific: boolean,
    existingSchedules: Map<string, Schedule>
  ): Schedule[] {
    const result: Schedule[] = []

    this.userSchedules.forEach((schedule, userId) => {
      const existingSchedule = existingSchedules.get(userId)
      
      let weeklyRules: DayAvailability[]
      let dateRules: DayAvailability[]

      if (updateWeekly) {
        // Use the new weekly schedule from bulk edit
        weeklyRules = this.weeklySchedule
          .filter(r => r.type === 'wday')
          .map(r => new DayAvailability({
            ...r,
            intervals: r.intervals.map(i => ({ ...i }))
          }))
      } else {
        // Preserve existing weekly schedule for this user
        weeklyRules = existingSchedule?.getWeeklySchedule()
          .map(r => new DayAvailability({
            ...r,
            intervals: r.intervals.map(i => ({ ...i }))
          })) || []
      }

      if (updateDateSpecific) {
        // Use the modified date-specific hours from the multiUserSchedule
        dateRules = schedule.rules
          .filter(r => r.type === 'date')
          .map(r => new DayAvailability({
            ...r,
            intervals: r.intervals.map(i => ({ ...i }))
          }))
      } else {
        // Preserve existing date-specific hours for this user
        dateRules = existingSchedule?.getDateSpecificHours()
          .map(r => new DayAvailability({
            ...r,
            intervals: r.intervals.map(i => ({ ...i }))
          })) || []
      }

      // Create new schedule combining both sections
      const newSchedule = new Schedule({
        userId,
        timezone: this.timezone,
        calendarIds: this.calendarId ? [this.calendarId] : [],
        rules: [...weeklyRules, ...dateRules],
        isCustom: true,
        isCreated: false,
        name: 'Custom Schedule',
      })

      result.push(newSchedule)
    })

    return result
  }

  /**
   * Get the weekly schedule (for comparison)
   */
  getWeeklySchedule(): DayAvailability[] {
    return this.weeklySchedule
  }

  /**
   * Get individual schedule for a specific user
   */
  getScheduleForUser(userId: string): Schedule | undefined {
    return this.userSchedules.get(userId)
  }

  /**
   * Get the initial weekly schedule for a specific user (for display purposes)
   */
  getInitialWeeklyScheduleForUser(userId: string): DayAvailability[] {
    const schedule = this.userSchedules.get(userId)
    if (!schedule) return []
    return schedule.getWeeklySchedule()
  }

  /**
   * Check if there are any date-specific hours across all users
   */
  hasAnyDateSpecificHours(): boolean {
    for (const schedule of this.userSchedules.values()) {
      if (schedule.getDateSpecificHours().length > 0) {
        return true
      }
    }
    return false
  }

  /**
   * Get total count of date-specific hours across all users
   */
  getTotalDateSpecificHoursCount(): number {
    let count = 0
    this.userSchedules.forEach(schedule => {
      count += schedule.getDateSpecificHours().length
    })
    return count
  }
}

